/** Client-side essential vs discretionary suggestion (mirrors backend heuristics). */

const DISCRETIONARY_HINTS = [
  'dining',
  'restaurant',
  'shopping',
  'entertainment',
  'recreation',
  'hobby',
  'hobbies',
  'subscription',
  'coffee',
  'bar',
  'fast food',
  'travel',
  'gift',
];

const ESSENTIAL_HINTS = [
  'rent',
  'housing',
  'mortgage',
  'utilities',
  'grocery',
  'groceries',
  'insurance',
  'transport',
  'commute',
  'health',
  'medical',
  'debt',
  'loan',
  'childcare',
];

export const SEED_EXPENSE_ROWS = [
  { id: 'rent', label: 'Rent / housing', essential: true, amount: '' },
  { id: 'utilities', label: 'Utilities', essential: true, amount: '' },
  { id: 'groceries', label: 'Groceries', essential: true, amount: '' },
  { id: 'transportation', label: 'Transportation', essential: true, amount: '' },
  { id: 'insurance', label: 'Insurance', essential: true, amount: '' },
  { id: 'dining', label: 'Dining out', essential: false, amount: '' },
  { id: 'subscriptions', label: 'Subscriptions', essential: false, amount: '' },
  { id: 'entertainment', label: 'Entertainment', essential: false, amount: '' },
  { id: 'shopping', label: 'Shopping', essential: false, amount: '' },
];

export function suggestEssential(label, tagOverrides = {}) {
  if (tagOverrides[label] !== undefined) {
    return tagOverrides[label];
  }
  const lower = (label || '').toLowerCase();
  if (DISCRETIONARY_HINTS.some((d) => lower.includes(d))) return false;
  if (ESSENTIAL_HINTS.some((h) => lower.includes(h))) return true;
  return false;
}

export function rowsFromParseResponse(data, tagOverrides = {}) {
  const parsed = (data?.rows || []).map((r, i) => ({
    id: `parsed-${i}-${r.label}`,
    label: r.label,
    amount: String(r.amount ?? ''),
    essential: r.essential ?? suggestEssential(r.label, tagOverrides),
  }));
  return parsed.length ? parsed : null;
}

const FREQ_MONTHLY_MULT = {
  weekly: 30 / 7,
  bi_weekly: 30 / 14,
  semi_monthly: 2,
  monthly: 1,
};

export function monthlyFromSources(sources) {
  let total = 0;
  (sources || []).forEach((s) => {
    const amt = parseFloat(s.amount) || 0;
    if (amt <= 0) return;
    const mult = FREQ_MONTHLY_MULT[s.frequency] || 30 / 14;
    total += amt * mult;
  });
  return total;
}

export function initEmployers(initialIncome) {
  const sources = initialIncome?.income_sources;
  if (sources?.length) {
    return sources.map((s, i) => ({
      id: `emp-${i}`,
      employer: s.employer || '',
      amount: String(s.amount ?? ''),
      frequency: s.frequency || 'monthly',
    }));
  }
  if (initialIncome?.average_amount) {
    return [
      {
        id: 'emp-0',
        employer: 'Primary income',
        amount: String(initialIncome.average_amount),
        frequency: initialIncome.frequency || 'monthly',
      },
    ];
  }
  return [{ id: 'emp-0', employer: '', amount: '', frequency: 'bi_weekly' }];
}

export const PAY_CYCLE_OPTIONS = [
  { value: '', label: 'Approximate (optional)' },
  { value: 'every_friday', label: 'Every Friday' },
  { value: 'every_other_friday', label: 'Every other Friday' },
  { value: '1st_and_15th', label: '1st and 15th of the month' },
  { value: 'monthly_1st', label: 'Once a month (1st)' },
  { value: 'monthly_15th', label: 'Once a month (15th)' },
];

export function computeTotals(incomeSources, rows) {
  const income = monthlyFromSources(incomeSources);
  let essential = 0;
  let discretionary = 0;
  rows.forEach((r) => {
    const amt = parseFloat(r.amount) || 0;
    if (amt <= 0) return;
    if (r.essential) essential += amt;
    else discretionary += amt;
  });
  return { income, essential, discretionary, surplus: Math.max(0, income - essential - discretionary) };
}
